import React, { useState, useEffect } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, Alert } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { GraphDataSelect } from './GraphDataSelect';
import { XYPlotSelect } from './XYPlotSelect';
import '../stylesheets/custom.css';
import '../stylesheets/buttons.css';

export function GraphTypeSelect({
    isOpen,
    toggle,
    selectedCompanyId = null,
    currentUsername = null,        // Logged-in user ID (required for feature check)
    currentCompanyId = null      // CompanyId of the current user (used for company-level feature check)
}) {
    const history = useHistory();

    const [timeDomainModalOpen, setTimeDomainModalOpen] = useState(false);
    const [xyPlotModalOpen, setXyPlotModalOpen] = useState(false);
    const [romanakModalOpen, setRomanakModalOpen] = useState(false);

    const [hasRomanakAccess, setHasRomanakAccess] = useState(false);
    const [loadingAccess, setLoadingAccess] = useState(true);
    const [accessError, setAccessError] = useState(null);

    // Check FeatureAccess using general endpoints (best for future extensibility)
    useEffect(() => {
        if (!isOpen) return;

        const checkFeatureAccess = async () => {
            setLoadingAccess(true);
            setAccessError(null);

            try {
                let response;
                let accessRecord = null;

                // 1. Try Company level first (higher priority)
                if (currentCompanyId !== 'null') {

                    const response = await fetch(`/api/FeatureAccess/company/${currentCompanyId}`, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`   // or however you store the JWT
                        }
                    });
                    if (response.ok) {
                        const data = await response.json();
                        accessRecord = Array.isArray(data) && data.length > 0 ? data[0] : null;
                    }
                }

                // 2. Fallback to User level if no company record or no companyId
                if (!accessRecord && currentUsername) {
                    const response = await fetch(`/api/FeatureAccess/username/${currentUsername}`, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`   // or however you store the JWT
                        }
                    });
                    if (response.ok) {
                        accessRecord = await response.json();
                    }
                }

                if (accessRecord?.features) {
                    // Support both spellings during transition
                    const romanakValue =
                        accessRecord.features.RomanakPlot ??
                        false;

                    setHasRomanakAccess(
                        romanakValue === true ||
                        romanakValue === "true" ||
                        romanakValue === "True" ||
                        romanakValue === "1"
                    );
                } else {
                    setHasRomanakAccess(false);
                }
            } catch (err) {
                console.error('Error fetching FeatureAccess for Romanak Plot:', err);
                setAccessError('Unable to check feature permissions. Romanak Plot may be unavailable.');
                setHasRomanakAccess(false); // Fail closed = safe default
            } finally {
                setLoadingAccess(false);
            }
        };

        checkFeatureAccess();
    }, [isOpen, currentCompanyId, currentUsername]);

    // Time Domain Graph flow
    const openTimeDomain = () => {
        toggle();
        setTimeDomainModalOpen(true);
    };

    const closeTimeDomain = () => setTimeDomainModalOpen(false);

    // X-Y Plot flow
    const openXYPlot = () => {
        toggle();
        setXyPlotModalOpen(true);
    };

    const closeXYPlot = () => setXyPlotModalOpen(false);

    // Romanak Plot flow
    const openRomanak = () => {
        if (!hasRomanakAccess) return;
        toggle();
        setRomanakModalOpen(true);
    };

    const closeRomanak = () => setRomanakModalOpen(false);

    const handleRomanakContinue = (selectedBoards, validPairs) => {
        closeRomanak();
        history.push('/romanakplot', {
            selectedBoards,
            pairs: validPairs
        });
    };

    return (
        <>
            {/* Graph Type Selection Modal */}
            <Modal
                isOpen={isOpen}
                toggle={toggle}
                size="lg"
                backdrop="static"
                centered
            >
                <ModalHeader toggle={toggle}>
                    Select Graph Type
                </ModalHeader>
                <ModalBody className="py-4">
                    <p className="text-center text-muted mb-4">
                        Choose the type of visualization you want to create from your board data.
                    </p>

                    {accessError && (
                        <Alert color="warning" className="mb-3">
                            {accessError}
                        </Alert>
                    )}

                    <Row className="g-3">
                        <Col xs={12}>
                            <Button
                                color="primary"
                                size="lg"
                                block
                                className="button-list py-3 mb-2"
                                onClick={openTimeDomain}
                            >
                                <strong>Time Domain Graph</strong>
                                <br />
                                <small>Plot selected data fields over time</small>
                            </Button>
                        </Col>

                        <Col xs={12}>
                            <Button
                                color="primary"
                                size="lg"
                                block
                                className="button-list py-3 mb-2"
                                onClick={openXYPlot}
                            >
                                <strong>X-Y Plot</strong>
                                <br />
                                <small>Plot one parameter vs. another</small>
                            </Button>
                        </Col>

                        {hasRomanakAccess && (
                            <Col xs={12}>
                                <Button
                                    color={hasRomanakAccess ? "primary" : "secondary"}
                                    size="lg"
                                    block
                                    className="button-list py-3"
                                    onClick={openRomanak}
                                    disabled={!hasRomanakAccess || loadingAccess}
                                >
                                    <strong>Romanak Plot</strong>
                                    <br />
                                    <small>
                                        {loadingAccess
                                            ? "Checking permissions..."
                                            : hasRomanakAccess
                                                ? "Specialized Romanak visualization with reference lines"
                                                : "Not available for your account • Contact admin"}
                                    </small>
                                </Button>
                            </Col>
                        )}
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggle}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Child Modals */}
            <GraphDataSelect
                isOpen={timeDomainModalOpen}
                toggle={closeTimeDomain}
                selectedCompanyId={selectedCompanyId}
            />

            <XYPlotSelect
                isOpen={xyPlotModalOpen}
                toggle={closeXYPlot}
                targetRoute="xyplot"
                selectedCompanyId={selectedCompanyId}
            />

            <XYPlotSelect
                isOpen={romanakModalOpen}
                toggle={closeRomanak}
                targetRoute="romanakplot"
                onContinue={handleRomanakContinue}
                selectedCompanyId={selectedCompanyId}
            />
        </>
    );
}